/**
 * Spinner — indeterminate loading indicator.
 *
 * Один знак — `progress_activity` из Material Symbols, вращающийся с
 * канонической частотой `motion.duration.loop` (1 с). Собственной графики
 * нет намеренно: индикатор наследует цвет и вес от текста рядом, поэтому
 * стоит в строке ровно как иконка, а не как чужой виджет.
 *
 * Крутился он в системе и раньше, но был заперт внутри `Button` — плашка,
 * карточка или строка списка воспользоваться им не могли и показывали
 * статичный знак. Теперь это атом, а кнопка — его первый потребитель.
 *
 * Доступность: по умолчанию индикатор объявлен `status` с подписью, чтобы
 * скринридер сказал «загрузка». Там, где рядом уже стоит текст состояния
 * («Собираем отчёт»), подпись — лишний повтор: передайте `label={null}`, и
 * знак станет декоративным.
 *
 * @format
 */

import {Box} from '@mui/material';
import {keyframes, styled} from '@mui/material/styles';

import {iconPxForRole} from 'ui/theme/typography';
import {MotionDurationLoop} from 'ui/tokens/ts/motion';

import {Icon} from '../Icon';

export const SPINNER_SIZES = ['s', 'm', 'l'] as const;
export type SpinnerSize = (typeof SPINNER_SIZES)[number];

/**
 * Ступени берутся у типографики, а не заводят свою шкалу: индикатор всегда
 * стоит рядом с текстом и обязан совпадать с ним по оптическому размеру.
 */
const SIZE_PX: Record<SpinnerSize, number> = {
  s: iconPxForRole('label.s'),
  m: iconPxForRole('label.m'),
  l: iconPxForRole('label.l'),
};

export interface SpinnerProps {
  /** Ступень размера или точное число пикселей. По умолчанию `m`. */
  size?: SpinnerSize | number;
  /**
   * Подпись для скринридера. `null` — знак декоративный: рядом уже есть
   * текст, объясняющий, что происходит.
   */
  label?: string | null;
  className?: string;
}

const spin = keyframes({
  to: {transform: 'rotate(360deg)'},
});

const Rotor = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  animation: `${spin} ${MotionDurationLoop} linear infinite`,
  // Пользователь мог попросить систему не двигать картинку. Тогда знак
  // остаётся на месте — статичный индикатор честнее отсутствующего.
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
});

export function Spinner({size = 'm', label = 'Загрузка', className}: SpinnerProps) {
  const px = typeof size === 'number' ? size : SIZE_PX[size];

  return (
    <Rotor
      className={className}
      role={label == null ? undefined : 'status'}
      aria-label={label ?? undefined}
      aria-hidden={label == null ? true : undefined}
    >
      <Icon name='progress_activity' size={px} color='inherit' />
    </Rotor>
  );
}
